import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import MatchList from "../components/Match/MatchList";
import {
  Layout,
  Row,
  Col,
  Card,
  Statistic,
  Avatar,
  Button,
  Space,
  Typography,
  Divider,
  Modal,
  Descriptions,
  Tag,
} from "antd";
import {
  TrophyOutlined,
  PlayCircleOutlined,
  PercentageOutlined,
  ClockCircleOutlined,
  UserOutlined,
  LogoutOutlined,
  TeamOutlined,
  CrownOutlined,
  MailOutlined,
  CalendarOutlined,
  SettingOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  AimOutlined,
  BookOutlined,
} from "@ant-design/icons";
import { fetchMatches } from "../api/services/matchfetchService";
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const Dashboard = () => {
  const { currentUser, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [userInfoModalVisible, setUserInfoModalVisible] = useState(false);

  useEffect(() => {
    const loadMatches = async () => {
      try {
        const data = await fetchMatches();
        setMatches(data);
      } catch (error) {
        console.error("Fehler beim Laden der Matches:", error);
      }
    };

    loadMatches();
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleExitApp = () => {
    Modal.confirm({
      title: 'App beenden',
      icon: <ExclamationCircleOutlined />,
      content: 'Möchten Sie die App wirklich beenden?',
      okText: 'Ja, beenden',
      okType: 'danger',
      cancelText: 'Abbrechen',
      onOk: async () => {
        if (Capacitor.isNativePlatform()) {
          await App.exitApp();
        }
      },
    });
  };

  // Berechne Statistiken
  const userMatches = matches.filter(
    (m) => {
      if (!currentUser) return false;
      // String-Vergleich (keine Number-Konvertierung)
      const userId = String(currentUser.id);
      const player1Id = String(m.player1);
      const player2Id = String(m.player2);
      return player1Id === userId || player2Id === userId;
    }
  );
  const noneUserMatches = matches.filter(
    (m) => {
      if (!currentUser) return false;
      // String-Vergleich (keine Number-Konvertierung)
      const userId = String(currentUser.id);
      const player1Id = String(m.player1);
      const player2Id = String(m.player2);
      return player1Id !== userId && player2Id !== userId;
    }
  );
  const wonMatches = userMatches.filter((m) => {
    if (!currentUser) return false;
    const userId = String(currentUser.id);
    const winnerId = String(m.winner);
    return winnerId === userId;
  });
  const openMatches = userMatches.filter((m) => !m.is_finished);
  const winRate =
    userMatches.length > 0
      ? ((wonMatches.length / userMatches.length) * 100).toFixed(1)
      : 0;

  return (
    <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
      {/* Header Section - Mobile Optimized */}
      <Header
        style={{
          background: "#fff",
          padding: "12px 16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          height: "auto",
          lineHeight: "normal",
        }}
      >
        <Row justify="space-between" align="middle" gutter={[8, 8]} wrap>
          <Col flex="auto">
            <Space size="middle">
              <Avatar
                size={{ xs: 40, sm: 48 }}
                style={{ backgroundColor: "#1890ff", cursor: "pointer" }}
                icon={<UserOutlined />}
                onClick={() => setUserInfoModalVisible(true)}
              >
                {currentUser?.name?.charAt(0).toUpperCase()}
              </Avatar>
              <div style={{ minWidth: 0, flex: 1 }}>
                <Title
                  level={5}
                  style={{
                    margin: 0,
                    fontSize: '16px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  Hi, {currentUser?.name?.split(' ')[0]}!
                </Title>
                {currentUser?.tenants && currentUser.tenants.length > 0 && (
                  <Text
                    type="secondary"
                    style={{
                      fontSize: '12px',
                      display: 'block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {currentUser.tenants.slice(0, 2).join(", ")}
                  </Text>
                )}
              </div>
            </Space>
          </Col>
          <Col flex="none">
            <Space size="small">
              <Button
                icon={<SettingOutlined />}
                onClick={() => navigate("/settings")}
                size="small"
              />
              {Capacitor.isNativePlatform() && (
                <Button
                  danger
                  icon={<CloseOutlined />}
                  onClick={handleExitApp}
                  size="small"
                  title="App beenden"
                />
              )}
              <Button
                type="primary"
                danger
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                size="small"
              />
            </Space>
          </Col>
        </Row>
      </Header>

      {/* Content Section - Mobile Optimized */}
      <Content style={{ padding: "16px", paddingBottom: "80px" }}>
        {/* Statistik Cards - 2x2 Grid on Mobile */}
        <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
          <Col xs={12} sm={12} lg={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                title="Gespielte Matches"
                value={userMatches.length}
                prefix={<PlayCircleOutlined />}
                valueStyle={{ color: "#1890ff", fontSize: '24px' }}
                formatter={(value) => <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{value}</div>}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                title="Gewonnene Matches"
                value={wonMatches.length}
                prefix={<TrophyOutlined />}
                valueStyle={{ color: "#52c41a", fontSize: '24px' }}
                formatter={(value) => <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{value}</div>}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                title="Siegesquote"
                value={winRate}
                prefix={<PercentageOutlined />}
                suffix="%"
                valueStyle={{ color: "#faad14", fontSize: '24px' }}
                formatter={(value) => <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{value}</div>}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Statistic
                title="Offene Matches"
                value={openMatches.length}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: "#722ed1", fontSize: '24px' }}
                formatter={(value) => <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{value}</div>}
              />
            </Card>
          </Col>
        </Row>

        {/* Quick Access Buttons */}
        <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
          <Col xs={12} sm={8} md={6}>
            <Card
              hoverable
              size="small"
              style={{ textAlign: 'center', background: '#e6f7ff', borderColor: '#1890ff' }}
              onClick={() => navigate('/equal-offense')}
            >
              <Space direction="vertical" size={4}>
                <AimOutlined style={{ fontSize: 28, color: '#1890ff' }} />
                <Text strong>Equal Offense</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>Training</Text>
              </Space>
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Card
              hoverable
              size="small"
              style={{ textAlign: 'center', background: '#f6ffed', borderColor: '#52c41a' }}
              onClick={() => navigate('/practice')}
            >
              <Space direction="vertical" size={4}>
                <BookOutlined style={{ fontSize: 28, color: '#52c41a' }} />
                <Text strong>Übungen</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>Bibliothek</Text>
              </Space>
            </Card>
          </Col>
        </Row>

        <Divider orientation="left" style={{ margin: '16px 0' }}>
          <Title level={4} style={{ margin: 0 }}>Meine Matches</Title>
        </Divider>

        {/* Match List */}
        <MatchList />
      </Content>

      {/* User Info Modal */}
      <Modal
        title={
          <Space>
            <Avatar
              size={40}
              style={{ backgroundColor: "#1890ff" }}
              icon={<UserOutlined />}
            >
              {currentUser?.name?.charAt(0).toUpperCase()}
            </Avatar>
            <span>Spieler-Informationen</span>
          </Space>
        }
        open={userInfoModalVisible}
        onCancel={() => setUserInfoModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setUserInfoModalVisible(false)}>
            Schließen
          </Button>,
        ]}
        width={600}
      >
        <Descriptions bordered column={1} style={{ marginTop: 16 }}>
          <Descriptions.Item label={<><UserOutlined /> Name</>}>
            <Text strong>{currentUser?.name}</Text>
          </Descriptions.Item>

          <Descriptions.Item label={<><MailOutlined /> E-Mail</>}>
            <Text>{currentUser?.email || "Keine E-Mail hinterlegt"}</Text>
          </Descriptions.Item>

          <Descriptions.Item label={<><TeamOutlined /> Tenants</>}>
            {currentUser?.tenants && currentUser.tenants.length > 0 ? (
              <Space wrap>
                {currentUser.tenants.map((tenant, index) => (
                  <Tag key={index} color="blue" icon={<TeamOutlined />}>
                    {tenant}
                  </Tag>
                ))}
              </Space>
            ) : (
              <Text type="secondary">Keine Tenants zugeordnet</Text>
            )}
          </Descriptions.Item>

          <Descriptions.Item label={<><CalendarOutlined /> Letztes Spiel</>}>
            {currentUser?.last_match_date && currentUser?.last_opponent ? (
              <Space>
                <Text>
                  {new Date(currentUser.last_match_date).toLocaleDateString('de-DE', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Text>
                <Text type="secondary">gegen</Text>
                <Text strong>{currentUser.last_opponent}</Text>
              </Space>
            ) : (
              <Text type="secondary">Noch keine Spiele</Text>
            )}
          </Descriptions.Item>

          <Descriptions.Item label={<><TrophyOutlined /> Gewonnene Matches</>}>
            <Text strong style={{ color: "#52c41a" }}>
              {currentUser?.won_matches || 0}
            </Text>
          </Descriptions.Item>

          <Descriptions.Item label={<><PlayCircleOutlined /> Gespielte Matches</>}>
            <Text strong style={{ color: "#1890ff" }}>
              {currentUser?.involved_matches || 0}
            </Text>
          </Descriptions.Item>

          {currentUser?.is_staff && (
            <Descriptions.Item label={<><CrownOutlined /> Status</>}>
              <Tag color="gold" icon={<CrownOutlined />}>
                Admin / Staff
              </Tag>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Modal>
    </Layout>
  );
};

export default Dashboard;